import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Divider,
    Stack,
    Tab,
    Tabs,
    Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FlussElementSelection } from "modules/fluss/types/fluss";
import {
    bindingTechnicalRows,
    FlussPanelNodeKind,
    nodeTypeLabelKey,
} from "./fluss-node-panel-utils";
import NodeOutputsSection from "./NodeOutputsSection";

export interface FlussNodePanelTab {
    key: string;
    label: ReactNode;
    content: ReactNode;
}

export interface FlussNodePanelShellProps {
    selection: FlussElementSelection;
    kind: FlussPanelNodeKind;
    tabs: FlussNodePanelTab[];
    canViewAdvanced: boolean;
    advancedContent?: ReactNode;
}

export const FlussNodePanelShell = ({
    selection,
    kind,
    tabs,
    canViewAdvanced,
    advancedContent,
}: FlussNodePanelShellProps) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState(tabs[0]?.key || "main");
    const visibleTabs = useMemo(() => [
        ...tabs,
        ...(canViewAdvanced ? [{
            key: "advanced",
            label: t("fluss.node_panel.tab.advanced", { defaultValue: "Advanced" }),
            content: (
                <Stack spacing={1.25}>
                    {advancedContent}
                <Accordion variant="outlined" defaultExpanded={false}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="body2" fontWeight={500}>
                            {t("fluss.node_panel.advanced.binding", {
                                defaultValue: "Technical binding",
                            })}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Stack spacing={0.75} data-testid="fluss-technical-binding">
                            {bindingTechnicalRows(selection.businessObject).length === 0 ? (
                                <Typography variant="caption" color="text.secondary">
                                    {t("fluss.node_panel.advanced.empty", {
                                        defaultValue: "No technical attributes configured.",
                                    })}
                                </Typography>
                            ) : bindingTechnicalRows(selection.businessObject).map((row) => (
                                <Box
                                    key={row.key}
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: "minmax(90px, 0.8fr) minmax(0, 1.2fr)",
                                        gap: 1,
                                    }}
                                >
                                    <Typography variant="caption" color="text.secondary">
                                        {row.key}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        component="code"
                                        sx={{ overflowWrap: "anywhere" }}
                                    >
                                        {row.value}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </AccordionDetails>
                </Accordion>
                </Stack>
            ),
        }] : []),
    ], [advancedContent, canViewAdvanced, selection.businessObject, t, tabs]);

    useEffect(() => {
        if (!visibleTabs.some((tab) => tab.key === activeTab)) {
            setActiveTab(visibleTabs[0]?.key || "main");
        }
    }, [activeTab, visibleTabs]);

    const active = visibleTabs.find((tab) => tab.key === activeTab) || visibleTabs[0];
    const nodeName = selection.businessObject?.name || selection.id;
    const typeFallbacks: Partial<Record<FlussPanelNodeKind, string>> = {
        message_start: "Início por mensagem",
        message_catch: "Wait for email",
        conditional_catch: "Aguardar condição",
        boundary_signal: "Sinal na borda",
        boundary_conditional: "Condição na borda",
        event_start_conditional: "Início condicional",
        script_task: "Tarefa de script",
    };
    const typeLabel = t(nodeTypeLabelKey(kind), { defaultValue: typeFallbacks[kind] || kind.replaceAll("_", " ") });
    const showTypeLabel = nodeName.trim().toLocaleLowerCase() !== String(typeLabel).trim().toLocaleLowerCase();

    return (
        <Box data-testid={`fluss-node-panel-${kind}`} className="fluss-node-properties">
            <Box className="fluss-node-properties__header">
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em" }}
                >
                    {t("fluss.node_panel.selected", { defaultValue: "Selected node" })}
                </Typography>
                <Stack direction="row" spacing={0.75} alignItems="baseline" minWidth={0}>
                    <Typography
                        title={nodeName}
                        sx={{
                            fontSize: 14,
                            fontWeight: 500,
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {nodeName}
                    </Typography>
                    {showTypeLabel && (
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                            {typeLabel}
                        </Typography>
                    )}
                </Stack>
            </Box>

            {visibleTabs.length >= 2 && (
                <>
                    <Tabs
                        value={active?.key}
                        onChange={(_, value) => setActiveTab(value)}
                        variant="scrollable"
                        scrollButtons={false}
                        className="fluss-node-properties__tabs"
                    >
                        {visibleTabs.map((tab) => (
                            <Tab key={tab.key} value={tab.key} label={tab.label} />
                        ))}
                    </Tabs>
                    <Divider />
                </>
            )}

            <Box className="fluss-node-properties__body">
                {active?.content}
                {active?.key !== "advanced" && <NodeOutputsSection />}
            </Box>
        </Box>
    );
};

export default FlussNodePanelShell;
